// 3D tilt on hover: the card rotates in perspective toward the cursor, easing back
// when the pointer leaves. Rotation is applied as an inline `transform`, which the
// scroll reveal (standalone `translate` property) never touches. The same loop
// drives a cursor-following spotlight overlay (effects.css `.tilt::before`)
// through the --spot-x/--spot-y/--spot-o custom properties.
//
// Max tilt angle in degrees comes from the card's --tilt-max custom property
// (unitless; see features.css for per-card overrides).

const DEFAULT_MAX_DEG = 4;
const SMOOTHING = 0.16; // per-frame lerp toward the target angle

export function initTilt(selector: string): void {
  if (
    !matchMedia('(hover: hover) and (pointer: fine)').matches ||
    matchMedia('(prefers-reduced-motion: reduce)').matches
  ) {
    return;
  }
  for (const card of document.querySelectorAll<HTMLElement>(selector)) {
    attachTilt(card);
  }
}

function attachTilt(card: HTMLElement): void {
  card.classList.add('tilt'); // enables the spotlight overlay (effects.css)
  const max = parseFloat(getComputedStyle(card).getPropertyValue('--tilt-max')) || DEFAULT_MAX_DEG;
  let targetX = 0;
  let targetY = 0;
  let currentX = 0;
  let currentY = 0;
  let spotTargetX = 50; // spotlight position, % of the card box
  let spotTargetY = 50;
  let spotX = 50;
  let spotY = 50;
  let raf = 0;
  let hovering = false;

  const frame = (): void => {
    currentX += (targetX - currentX) * SMOOTHING;
    currentY += (targetY - currentY) * SMOOTHING;
    spotX += (spotTargetX - spotX) * SMOOTHING;
    spotY += (spotTargetY - spotY) * SMOOTHING;
    if (!hovering && Math.abs(currentX) < 0.02 && Math.abs(currentY) < 0.02) {
      currentX = currentY = 0;
      raf = 0;
      card.style.transform = '';
      card.style.willChange = '';
      card.style.removeProperty('--spot-x');
      card.style.removeProperty('--spot-y');
      card.style.removeProperty('--spot-o');
      return;
    }
    card.style.transform = `perspective(900px) rotateX(${currentX.toFixed(3)}deg) rotateY(${currentY.toFixed(3)}deg)`;
    card.style.setProperty('--spot-x', `${spotX.toFixed(2)}%`);
    card.style.setProperty('--spot-y', `${spotY.toFixed(2)}%`);
    raf = requestAnimationFrame(frame);
  };

  const setTargets = (event: PointerEvent): void => {
    const rect = card.getBoundingClientRect();
    const px = (event.clientX - rect.left) / rect.width; // 0 left … 1 right
    const py = (event.clientY - rect.top) / rect.height; // 0 top … 1 bottom
    targetY = (px * 2 - 1) * max; // cursor right → right edge toward the viewer
    targetX = -(py * 2 - 1) * max; // cursor top → top edge toward the viewer
    spotTargetX = px * 100;
    spotTargetY = py * 100;
  };

  card.addEventListener('pointerenter', (event) => {
    hovering = true;
    setTargets(event);
    spotX = spotTargetX; // the light appears where the cursor entered, no glide-in
    spotY = spotTargetY;
    card.style.setProperty('--spot-o', '1');
    card.style.willChange = 'transform';
    if (!raf) raf = requestAnimationFrame(frame);
  });

  card.addEventListener('pointermove', setTargets);

  card.addEventListener('pointerleave', () => {
    hovering = false;
    targetX = targetY = 0;
    card.style.setProperty('--spot-o', '0');
  });
}
